import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DATE_FORMAT_MMMM_YYYY, LOCALE_CA, LOCALE_ES } from '@app/@core/constants/common.constants';
import { LANGUAGE_CATALAN, LANGUAGE_SPANISH } from '@app/@core/constants/requests.constants';
import { TransformNumberPipe } from '@app/@shared/pipes/transform-number.pipe';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { TranslateService } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { ControlPanelStore } from '../control-panel-service/control-panel.store';
import { InvestDetailOperationAgent } from '../models/control-panel.model';

@Component({
  selector: 'app-invest-detail',
  templateUrl: './invest-detail.component.html',
  styleUrls: ['./invest-detail.component.scss'],
})
export class InvestDetailComponent implements OnInit {
  data: InvestDetailOperationAgent[] = [];
  numUsers: number;
  formData: FormGroup;

  private division: string;

  public head = [
    'investDetail.sellerHeader',
    'investDetail.codeHeader',
    'investDetail.clientHeader',
    'investDetail.prodHeader',
    'investDetail.dateFormHeader',
    'investDetail.investmentHeader',
    'investDetail.tinHeader',
    'investDetail.incentiveHeader',
    'investDetail.termHeader',
    'investDetail.typeHeader',
    'investDetail.brandHeader',
    'investDetail.modelHeader',
    'investDetail.protHeader',
    'investDetail.rappHeader',
  ];

  constructor(
    private excelService: ExcelService,
    private controlPanelStore: ControlPanelStore,
    private transformNumberPipe: TransformNumberPipe,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {}

  loadDataTable(data: any): void {
    if (data && Object.keys(data).length > 0) {
      this.division = data.division;
      this.formData = data.form;
      this.controlPanelStore.getInvestmentDetailReport(data?.form).then(
        (data: InvestDetailOperationAgent[]) => {
          this.data = data;
        },
        (error) => console.error(error)
      );
    }
  }

  createPdf() {
    try {
      let doc = new jsPDF({
        orientation: 'landscape',
        format: [296, 209],
      });

      const date = moment().locale('es');
      const filename =
        this.translateService.instant('excel.inversionDO.filename') + moment().locale('es').format('DDMMYYYY');

      doc.setProperties({
        title: filename,
      });

      let dataPdf: any[] = [];

      /* BODY */
      let incentive: number = 0;
      let investment: number = 0;
      this.data.forEach((item) => {
        const itemObjects = item.dataGroup.map((value) => {
          return {
            userDesc: value.userDesc,
            contractId: value.contractId,
            holderName: value.holderName,
            finProd: value.finProd,
            autDate: value.autDate,
            invest: this.transformNumberPipe.transform(value.invest),
            tin: this.transformNumberPipe.transform(value.tin),
            incentive: this.transformNumberPipe.transform(value.incentive),
            deadlines: value.deadlines,
            description: value.description,
            brand: value.brand,
            model: value.model,
            strSeg: value.strSeg,
            rappel: value.rappel,
          };
        });
        dataPdf.push(...itemObjects);
        incentive += item.totals.totalIncent;
        investment += item.totals.totalInvest;
      });
      dataPdf.push({
        userDesc: 'Total',
        contractId: '',
        holderName: '',
        finProd: '',
        autDate: '',
        invest: this.transformNumberPipe.transform(investment) + ' Euros',
        tin: '',
        incentive: this.transformNumberPipe.transform(incentive) + ' Euros',
        deadlines: '',
        description: '',
        brand: '',
        model: '',
        strSeg: '',
        rappel: '',
      });

      autoTable(doc, {
        startY: 55,
        head: [this.head.map((header) => this.translateService.instant(header))],
        headStyles: { fillColor: '#969696' },
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        margin: { top: 55 },
        body: dataPdf.map(Object.values),
        didDrawPage: (data) => {
          doc.setFontSize(24);
          doc.setTextColor('1E90FF');

          const text = this.translateService.instant('excel.dashboard.title');
          if (text.length <= 27) doc.text(text, 160, 25);
          else doc.text(text, 140, 25);

          doc.setTextColor('#000000');
          doc.setFontSize(16);
          doc.text(this.translateService.instant('excel.inversionDO.list'), 14, 52);
          doc.setFontSize(12);
          doc.text(
            'De ' + this.getInitialDate(DATE_FORMAT_MMMM_YYYY) + ' a ' + this.getFinalDate(DATE_FORMAT_MMMM_YYYY),
            110,
            37
          );
          doc.text(this.translateService.instant('excel.division') + this.division, 14, 45);
          doc.text(this.translateService.instant('excel.date') + date.format('DD-MM-YYYY HH:mm:ss'), 220, 37);

          doc.text(this.translateService.instant('excel.pag') + ' ' + data.pageNumber, 220, 45);

          var img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 10, 80, 30);
        },
      });

      var blob = doc.output('blob');
      FileSaver.saveAs(blob, filename);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  createExcel() {
    const dataForExcel: any[] = [];
    const filename =
      this.translateService.instant('excel.inversionDO.filename') + moment().locale('es').format('DDMMYYYY');
    const date = [this.getInitialDate(DATE_FORMAT_MMMM_YYYY), this.getFinalDate(DATE_FORMAT_MMMM_YYYY)];

    const incentive: number = this.data.map((item) => item.totals.totalIncent).reduce((a, b) => a + b);
    const investment: number = this.data.map((item) => item.totals.totalInvest).reduce((a, b) => a + b);

    this.data.forEach((item) => {
      item.dataGroup.forEach((value) => {
        const obj = {
          userDesc: value.userDesc,
          contractId: value.contractId,
          holderName: value.holderName,
          finProd: value.finProd,
          autDate: value.autDate,
          invest: value.invest,
          tin: value.tin,
          incentive: value.incentive,
          deadlines: value.deadlines,
          description: value.description,
          brand: value.brand,
          model: value.model,
          strSeg: value.strSeg,
          rappel: value.rappel,
        };
        dataForExcel.push(Object.values(obj));
      });
    });

    dataForExcel.push(
      Object.values({
        investment: investment,
        incentive: incentive,
      })
    );

    const excelData = {
      headers: this.head.map((header) => this.translateService.instant(header)),
      fileName: filename,
      data: dataForExcel,
      division: this.division,
      date: date,
    };
    this.excelService.exportExcelDetailOperations(excelData);
  }

  private getInitialDate(format: string) {
    return this.formData
      ?.get('initialDate')
      ?.value.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }

  private getFinalDate(format: string) {
    return this.formData
      ?.get('finalDate')
      ?.value.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }
}
